// src/poses/norwegianTargets.js
// ----------------------------------------------------------------------------
// Feature-driven targets (canonical) + SVG render compatibility layer
//
// FORMAT YOU WANTED:
// - Stops explicitly define timing.durations using STOP_DUR (no inference).
// - All entries are guaranteed to have `render` so ArticulationSideView never crashes.
// - Non-stops get a conservative fallback `render` + default durations
//   (but we do NOT change your existing feature fields).
//
// Renderer contract (ArticulationSideView):
//   target.render = {
//     rest?:    { show: string[] },
//     closure?: { show: string[] },
//     release?: { show: string[], burst?: "strong"|"weak", voicing?: boolean },
//     static?:  { show: string[] } // for non-stops
//   }
//
// Also supported by ArticulationSideView:
//   target.timing.durations = { rest, closure, release, static, burst, transition }
// ----------------------------------------------------------------------------

// --- stops (durations aligned with agreed model) ----------------------------
// Model:
// - rest      = approach into closure (40–70ms)
// - closure   = closure hold
// - burst     = one-shot airflow/voicing window (10–20ms)
// - transition= post-release shaping into next segment (40–90ms)
// - release   = how long the RELEASE STAGE stays on screen in the renderer
//              (today: we approximate releaseStage = burst + transition)

const STOP_DUR = {
  voiceless: {
    rest: 55,        // approach
    closure: 75,     // hold
    burst: 14,       // one-shot effect window
    transition: 70,  // into vowel
    release: 84,     // = burst + transition (stage duration)
  },
  voiced: {
    rest: 55,        // approach
    closure: 50,     // shorter hold
    burst: 12,       // one-shot effect window
    transition: 60,  // into vowel
    release: 72,     // = burst + transition (stage duration)
  },
};

// --- Non-stop defaults (UI display timing, not phonetic duration) ----------
const NONSTOP_DUR = {
  consonant: { static: 180, rest: 90 },
  vowelShort: { static: 220, rest: 90 },
  vowelLong: { static: 320, rest: 90 },
  diphthong: { static: 360, rest: 90 },
};

// ---- Canonical feature definitions (yours) --------------------------------
const RAW = {
  // --- STOPS (explicit durations in the agreed format) ----------------------

  p: {
    render: {
      rest:    { show: ["upper_jaw_lips_open_velum_rest", "lower_jaw_lips_open"] },
      closure: { show: ["upper_jaw_lips_closed_velum_rest", "lower_jaw_lips_closed"] },
      release: {
        show: ["upper_jaw_lips_open_velum_raised", "lower_jaw_lips_open"],
        burst: "strong",
        voicing: false,
      },
    },
    lips: "closed",
    velum: "raised",
    tip: "neutral",
    body: "neutral",
    jaw: "neutral",
    timing: {
      burst: "strong",
      voicing: "off",
      durations: { ...STOP_DUR.voiceless },
    },
  },

  b: {
    render: {
      rest:    { show: ["upper_jaw_lips_open_velum_rest", "lower_jaw_lips_open"] },
      closure: { show: ["upper_jaw_lips_closed_velum_rest", "lower_jaw_lips_closed"] },
      release: {
        show: ["upper_jaw_lips_open_velum_raised", "lower_jaw_lips_open"],
        burst: "weak",
        voicing: true,
      },
    },
    lips: "closed",
    velum: "raised",
    tip: "neutral",
    body: "neutral",
    jaw: "neutral",
    timing: {
      burst: "weak",
      voicing: "on",
      durations: { ...STOP_DUR.voiced },
    },
  },

  t: {
    render: {
      rest:    { show: ["upper_jaw_lips_open_velum_rest", "lower_jaw_lips_open"] },
      closure: { show: ["upper_jaw_lips_open_velum_rest", "lower_jaw_lips_open_tip_alveolar"] },
      release: {
        show: ["upper_jaw_lips_open_velum_raised", "lower_jaw_lips_open"],
        burst: "strong",
        voicing: false,
      },
    },
    lips: "open",
    velum: "raised",
    tip: "alveolar",
    body: "neutral",
    jaw: "neutral",
    timing: {
      burst: "strong",
      voicing: "off",
      durations: { ...STOP_DUR.voiceless },
    },
  },

  d: {
    render: {
      rest:    { show: ["upper_jaw_lips_open_velum_rest", "lower_jaw_lips_open"] },
      closure: { show: ["upper_jaw_lips_open_velum_rest", "lower_jaw_lips_open_tip_alveolar"] },
      release: {
        show: ["upper_jaw_lips_open_velum_raised", "lower_jaw_lips_open"],
        burst: "weak",
        voicing: true,
      },
    },
    lips: "open",
    velum: "raised",
    tip: "alveolar",
    body: "neutral",
    jaw: "neutral",
    timing: {
      burst: "weak",
      voicing: "on",
      durations: { ...STOP_DUR.voiced },
    },
  },

  k: {
    render: {
      rest:    { show: ["upper_jaw_lips_open_velum_rest", "lower_jaw_lips_open"] },
      closure: { show: ["upper_jaw_lips_open_velum_rest", "lower_jaw_lips_open_tongue_back_high"] },
      release: {
        show: ["upper_jaw_lips_open_velum_raised", "lower_jaw_lips_open"],
        burst: "strong",
        voicing: false,
      },
    },
    lips: "open",
    velum: "raised",
    tip: "neutral",
    body: "back_high",
    jaw: "neutral",
    timing: {
      burst: "strong",
      voicing: "off",
      durations: { ...STOP_DUR.voiceless },
    },
  },

  "ɡ": {
    render: {
      rest:    { show: ["upper_jaw_lips_open_velum_rest", "lower_jaw_lips_open"] },
      closure: { show: ["upper_jaw_lips_open_velum_rest", "lower_jaw_lips_open_tongue_back_high"] },
      release: {
        show: ["upper_jaw_lips_open_velum_raised", "lower_jaw_lips_open"],
        burst: "weak",
        voicing: true,
      },
    },
    lips: "open",
    velum: "raised",
    tip: "neutral",
    body: "back_high",
    jaw: "neutral",
    timing: {
      burst: "weak",
      voicing: "on",
      durations: { ...STOP_DUR.voiced },
    },
  },

  // --- NASALS ---------------------------------------------------------------
  m: { lips: "closed", velum: "lowered", tip: "neutral", body: "neutral", jaw: "neutral", timing: { voicing: "on" } },
  n: { lips: "open", velum: "lowered", tip: "alveolar", body: "neutral", jaw: "neutral", timing: { voicing: "on" } },
  "ŋ": { lips: "open", velum: "lowered", tip: "neutral", body: "back_high", jaw: "neutral", timing: { voicing: "on" } },

  // --- FRICATIVES / APPROXIMANTS -------------------------------------------
  f: { lips: "open", velum: "raised", tip: "neutral", body: "neutral", jaw: "neutral" },
  "ʋ": { lips: "open", velum: "raised", tip: "neutral", body: "neutral", jaw: "neutral", timing: { voicing: "on" } },
  s: { lips: "open", velum: "raised", tip: "alveolar", body: "neutral", jaw: "neutral" },
  "ʃ": { lips: "open", velum: "raised", tip: "retroflex", body: "neutral", jaw: "neutral" },
  "ç": { lips: "open", velum: "raised", tip: "neutral", body: "front_high", jaw: "neutral" },
  j: { lips: "open", velum: "raised", tip: "neutral", body: "front_high", jaw: "neutral", timing: { voicing: "on" } },
  h: { lips: "open", velum: "raised", tip: "neutral", body: "neutral", jaw: "neutral" },

  // --- LIQUIDS --------------------------------------------------------------
  r: { lips: "open", velum: "raised", tip: "alveolar", body: "neutral", jaw: "neutral", timing: { voicing: "on" } },
  l: { lips: "open", velum: "raised", tip: "alveolar", body: "neutral", jaw: "neutral", timing: { voicing: "on" } },

  // --- RETROFLEX SERIES -----------------------------------------------------
  "ʈ": { lips: "open", velum: "raised", tip: "retroflex", body: "neutral", jaw: "neutral", timing: { burst: "strong" } },
  "ɖ": { lips: "open", velum: "raised", tip: "retroflex", body: "neutral", jaw: "neutral", timing: { voicing: "on" } },
  "ɳ": { lips: "open", velum: "lowered", tip: "retroflex", body: "neutral", jaw: "neutral", timing: { voicing: "on" } },
  "ɭ": { lips: "open", velum: "raised", tip: "retroflex", body: "neutral", jaw: "neutral", timing: { voicing: "on" } },

  // --- VOWELS (feature-only, for now) --------------------------------------
  i:    { lips: "open", velum: "raised", tip: "neutral", body: "front_high", jaw: "neutral" },
  "iː": { lips: "open", velum: "raised", tip: "neutral", body: "front_high", jaw: "neutral", timing: { length: "long" } },

  y:    { lips: "rounded", lipShape: "protruded", velum: "raised", tip: "neutral", body: "front_high", jaw: "neutral" },
  "yː": { lips: "rounded", lipShape: "protruded", velum: "raised", tip: "neutral", body: "front_high", jaw: "neutral", timing: { length: "long" } },

  e:    { lips: "open", velum: "raised", tip: "neutral", body: "front_mid", jaw: "neutral" },
  "eː": { lips: "open", velum: "raised", tip: "neutral", body: "front_mid", jaw: "neutral", timing: { length: "long" } },

  "ø":   { lips: "rounded", lipShape: "protruded", velum: "raised", tip: "neutral", body: "front_mid", jaw: "neutral" },
  "øː":  { lips: "rounded", lipShape: "protruded", velum: "raised", tip: "neutral", body: "front_mid", jaw: "neutral", timing: { length: "long" } },

  "æ":   { lips: "open", velum: "raised", tip: "neutral", body: "front_low", jaw: "lowered" },
  "æː":  { lips: "open", velum: "raised", tip: "neutral", body: "front_low", jaw: "lowered", timing: { length: "long" } },

  "ɑ":   { lips: "open", velum: "raised", tip: "neutral", body: "back_low", jaw: "lowered" },
  "ɑː":  { lips: "open", velum: "raised", tip: "neutral", body: "back_low", jaw: "lowered", timing: { length: "long" } },

  "ʉ":   { lips: "rounded", lipShape: "retracted", velum: "raised", tip: "neutral", body: "central_high", jaw: "neutral" },
  "ʉː":  { lips: "rounded", lipShape: "retracted", velum: "raised", tip: "neutral", body: "central_high", jaw: "neutral", timing: { length: "long" } },

  u:     { lips: "rounded", lipShape: "protruded", velum: "raised", tip: "neutral", body: "back_high", jaw: "neutral" },
  "uː":  { lips: "rounded", lipShape: "protruded", velum: "raised", tip: "neutral", body: "back_high", jaw: "neutral", timing: { length: "long" } },

  o:     { lips: "rounded", lipShape: "protruded", velum: "raised", tip: "neutral", body: "back_mid", jaw: "neutral" },
  "oː":  { lips: "rounded", lipShape: "protruded", velum: "raised", tip: "neutral", body: "back_mid", jaw: "neutral", timing: { length: "long" } },

  // --- DIPHTHONGS -----------------------------------------------------------
  ei: {
    type: "diphthong",
    glide: {
      start: { lips: "open", velum: "raised", tip: "neutral", body: "front_mid",  jaw: "neutral" },
      end:   { lips: "open", velum: "raised", tip: "neutral", body: "front_high", jaw: "neutral" },
    },
  },
  "æi": {
    type: "diphthong",
    glide: {
      start: { lips: "open", velum: "raised", tip: "neutral", body: "front_low",  jaw: "lowered" },
      end:   { lips: "open", velum: "raised", tip: "neutral", body: "front_high", jaw: "neutral" },
    },
  },
  "ɑi": {
    type: "diphthong",
    glide: {
      start: { lips: "open", velum: "raised", tip: "neutral", body: "back_low",   jaw: "lowered" },
      end:   { lips: "open", velum: "raised", tip: "neutral", body: "front_high", jaw: "neutral" },
    },
  },
  "øʉ": {
    type: "diphthong",
    glide: {
      start: { lips: "rounded", lipShape: "protruded", velum: "raised", tip: "neutral", body: "front_mid",    jaw: "neutral" },
      end:   { lips: "rounded", lipShape: "retracted", velum: "raised", tip: "neutral", body: "central_high", jaw: "neutral" },
    },
  },
  "æʉ": {
    type: "diphthong",
    glide: {
      start: { lips: "open", velum: "raised", tip: "neutral", body: "front_low", jaw: "lowered" },
      end:   { lips: "rounded", lipShape: "retracted", velum: "raised", tip: "neutral", body: "central_high", jaw: "neutral" },
    },
  },
  "øy": {
    type: "diphthong",
    glide: {
      start: { lips: "rounded", lipShape: "protruded", velum: "raised", tip: "neutral", body: "front_mid",  jaw: "neutral" },
      end:   { lips: "rounded", lipShape: "protruded", velum: "raised", tip: "neutral", body: "front_high", jaw: "neutral" },
    },
  },
  oy: {
    type: "diphthong",
    glide: {
      start: { lips: "rounded", lipShape: "protruded", velum: "raised", tip: "neutral", body: "back_mid",   jaw: "neutral" },
      end:   { lips: "rounded", lipShape: "protruded", velum: "raised", tip: "neutral", body: "front_high", jaw: "neutral" },
    },
  },
};

// ---- Render fallback builder -----------------------------------------------

const LAYERS = {
  upper_open_rest:   "upper_jaw_lips_open_velum_rest",
  upper_closed_rest: "upper_jaw_lips_closed_velum_rest",
  upper_open_raised: "upper_jaw_lips_open_velum_raised",
  lower_open:        "lower_jaw_lips_open",
  lower_closed:      "lower_jaw_lips_closed",
};

function inferVoicing(entry) {
  const v = entry?.timing?.voicing;
  return v === "on" || v === true || v === "true";
}

function inferBurst(entry) {
  const b = entry?.timing?.burst;
  return b === "strong" || b === "weak" ? b : null;
}

function inferLipClosure(entry) {
  return String(entry?.lips || "").toLowerCase() === "closed";
}

function buildFallbackRender(entry) {
  // If author provided render already, keep it.
  if (entry?.render && typeof entry.render === "object") return entry.render;

  const voicing = inferVoicing(entry);
  const lipsClosed = inferLipClosure(entry);

  // Non-stops: stable pose (we don't guess tongue layers here)
  const staticShow = lipsClosed
    ? [LAYERS.upper_closed_rest, LAYERS.lower_closed]
    : [LAYERS.upper_open_raised, LAYERS.lower_open];

  return {
    static: {
      show: staticShow,
      ...(voicing ? { voicing: true } : {}),
    },
    rest: {
      show: staticShow,
      ...(voicing ? { voicing: true } : {}),
    },
  };
}

// ---- Duration fallback for non-stops ---------------------------------------

function ensureDurations(ipa, entry) {
  // If durations already exist (stops, or user-defined), do nothing.
  if (entry?.timing?.durations && typeof entry.timing.durations === "object") {
    return entry.timing.durations;
  }

  const isDiph = entry?.type === "diphthong";
  const isLong = String(ipa || "").includes("ː") || entry?.timing?.length === "long";

  const vowelBase = String(ipa || "").replace("ː", "");
  const isVowel =
    ["i","y","e","ø","æ","ɑ","ʉ","u","o"].includes(vowelBase);

  if (isDiph) return { ...NONSTOP_DUR.diphthong };
  if (isVowel) return { ...(isLong ? NONSTOP_DUR.vowelLong : NONSTOP_DUR.vowelShort) };
  return { ...NONSTOP_DUR.consonant };
}

// ---- Export: feature-first objects + guaranteed `.render` + durations -------

export const NO_TARGETS = Object.fromEntries(
  Object.entries(RAW).map(([ipa, entry]) => {
    const safe = { ...(entry || {}) };

    // Ensure timing exists
    safe.timing = { ...(safe.timing || {}) };

    // Ensure render exists (stops already have render, non-stops get fallback)
    safe.render = buildFallbackRender(safe);

    // Ensure durations exist (stops already explicit; non-stops get defaults)
    safe.timing.durations = ensureDurations(ipa, safe);

    return [ipa, safe];
  })
);